import { StrictMode, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  App,
  LocalSearchProvider,
  SetupPanel,
  VOLUME_SPECS,
  createLocalBasketStorage,
  initialVolumeStates,
  type BatchResult,
  type BasketStorage,
  type CaseTextDocument,
  type Dossier,
  type Ranker,
  type SetupVolume,
  type VolumeKey,
  type VolumeStates,
} from "@beoptong/core";
import { WorkerSearchTransport } from "./search-transport";
import type { VolumeStatus } from "./search-protocol";
import { LocalVolumeStore } from "./volume-store";
import "./demo.css";

/**
 * KURE 판정 모델은 이 데모에 넣지 않는다(모델 다운로드 없음).
 * 결과는 evidence-first(ResultsView)로 렌더되므로 배지 판정은 쓰이지 않는다.
 * backend "webgpu"는 App의 상단 상태등을 "오프라인"으로 표시하기 위한 값이다.
 */
class PassthroughRanker implements Ranker {
  readonly backend = "webgpu";

  async init(): Promise<void> {
    // no-op
  }

  // eslint-disable-next-line require-yield
  async *rerank(): AsyncIterable<BatchResult> {
    // 판정 없음: useDossier는 즉시 done으로 넘어간다.
  }
}

const searchWorker = new Worker(new URL("search.worker.js", document.baseURI), {
  type: "module",
  name: "beoptong-search",
});
const transport = new WorkerSearchTransport(searchWorker);
const provider = new LocalSearchProvider(transport);
const ranker = new PassthroughRanker();

/** 데모의 인용 바구니는 localStorage에 둔다(확장은 chrome.storage.local). */
const basketStorage: BasketStorage = createLocalBasketStorage();

type Frame = "wide" | "tablet" | "panel";

const FRAMES: Array<{ id: Frame; label: string }> = [
  { id: "wide", label: "넓게" },
  { id: "tablet", label: "태블릿" },
  { id: "panel", label: "사이드패널" },
];

const REQUIRED_TOTAL_BYTES = VOLUME_SPECS.filter((spec) => spec.required).reduce(
  (sum, spec) => sum + spec.sizeBytes,
  0,
);

const DEMO_NOTE = "로컬 데모 · 파일은 브라우저 밖으로 나가지 않습니다";

/** 개발/프로브용. 실제 UI 상태의 단일 출처를 그대로 읽는다. */
let activeStore: LocalVolumeStore | null = null;

/** 두 볼륨이 거의 동시에 끝나도 워커 reopen이 겹치지 않게 직렬화한다. */
let reopenChain: Promise<unknown> = Promise.resolve();

function toSetupVolumes(states: VolumeStates): SetupVolume[] {
  return VOLUME_SPECS.map((spec) => {
    const state = states[spec.key];
    return {
      id: spec.key,
      name: spec.name,
      detail: spec.detail,
      sizeBytes: state.total > 0 ? state.total : spec.sizeBytes,
      installedBytes: state.phase === "installed" ? state.bytes : undefined,
      phase: state.phase,
      bytes: state.bytes,
      bytesPerSecond: state.rate,
      etaSeconds: state.eta ?? null,
      error: state.error,
      source: state.source,
      importable: spec.importable,
    };
  });
}

function Root() {
  const [volumes, setVolumes] = useState<VolumeStatus | null>(null);
  const [volumeStates, setVolumeStates] = useState<VolumeStates>(() => initialVolumeStates());
  const [storeReady, setStoreReady] = useState(false);
  const [started, setStarted] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [frame, setFrame] = useState<Frame>("wide");
  const storeRef = useRef<LocalVolumeStore | null>(null);

  // 워커가 이미 SAHPool/OPFS에 볼륨을 갖고 있으면(이전 세션) 바로 상태를 읽는다.
  useEffect(() => {
    let cancelled = false;
    void transport
      .status()
      .then((next) => {
        if (!cancelled) setVolumes(next);
      })
      .catch((error: unknown) => {
        console.error("[beoptong] search worker status failed", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let store: LocalVolumeStore | null = null;

    void LocalVolumeStore.create({
      onState: (next) => {
        if (!cancelled) setVolumeStates(next);
      },
      onInstalled: (key) => {
        if (cancelled) return;
        reopenChain = reopenChain
          .then(async () => {
            if (cancelled) return;
            const next = await transport.reopen([key]);
            if (!cancelled) setVolumes(next);
          })
          .catch((error: unknown) => {
            console.error("[beoptong] search worker reopen failed", key, error);
          });
      },
    })
      .then((created) => {
        if (cancelled) {
          void created.dispose();
          return;
        }
        store = created;
        storeRef.current = created;
        activeStore = created;
        setVolumeStates(created.states());
        setStoreReady(true);
      })
      .catch((error: unknown) => {
        console.error("[beoptong] volume store init failed", error);
        if (!cancelled) setStoreReady(true);
      });

    return () => {
      cancelled = true;
      storeRef.current = null;
      if (activeStore === store) activeStore = null;
      void store?.dispose();
    };
  }, []);

  const importVolume = (id: string, file: File) => {
    const key = id as VolumeKey;
    void storeRef.current?.importFile(key, file).catch((error: unknown) => {
      console.error("[beoptong] volume import failed", key, error);
    });
  };

  const setupVolumes = toSetupVolumes(volumeStates);
  const coreReady = volumes?.core === true;
  const setupPhase = coreReady ? ("ready" as const) : ("welcome" as const);

  const volumeHandlers = {
    onVolumePause: (id: string) => storeRef.current?.pause(id as VolumeKey),
    onVolumeCancel: (id: string) => {
      void storeRef.current?.cancel(id as VolumeKey);
    },
    onVolumeImport: importVolume,
  };

  /** 아직 설치되지 않은 첫 볼륨의 파일 선택기를 연다. */
  const pickFirstMissing = () => {
    const rows = [...document.querySelectorAll<HTMLElement>(".setup-file")];
    const row = rows.find((item) => {
      const status = item.querySelector(".setup-file-status")?.textContent;
      return item.querySelector("input[type=file]") && status !== "설치됨";
    });
    row?.querySelector<HTMLInputElement>("input[type=file]")?.click();
  };

  if (!started) {
    if (!storeReady) {
      return <div className="demo-boot">볼륨 저장소 확인 중…</div>;
    }
    return (
      <div className="demo-gate">
        <div className="demo-note">
          <strong>로컬 데모</strong> · 네트워크 다운로드가 없습니다. 각 볼륨의{" "}
          <b>파일에서 가져오기</b>로 로컬 <code>.aknpkg</code>를 선택하세요. 파일은
          브라우저 밖으로 나가지 않습니다.
        </div>
        <SetupPanel
          mock={false}
          modelReady
          showModel={false}
          startLabel="오프라인 데이터 가져오기"
          allowSkip={false}
          status={{
            phase: setupPhase,
            bytes: 0,
            totalBytes: REQUIRED_TOTAL_BYTES,
            showCorpus: true,
          }}
          volumes={setupVolumes}
          onStart={pickFirstMissing}
          onComplete={() => setStarted(true)}
          {...volumeHandlers}
        />
      </div>
    );
  }

  return (
    <div className="demo-stage">
      <div className="demo-toolbar" role="group" aria-label="화면 폭">
        <span className="demo-toolbar-label">화면</span>
        {FRAMES.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`demo-frame-button${frame === option.id ? " is-active" : ""}`}
            aria-pressed={frame === option.id}
            onClick={() => setFrame(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className={`demo-frame demo-frame-${frame}`}>
        <App
          provider={provider}
          ranker={ranker}
          notice={DEMO_NOTE}
          onOpenSetup={() => setShowSetup(true)}
          basketStorage={basketStorage}
        />
      </div>
      <button
        type="button"
        className="demo-data-button"
        onClick={() => setShowSetup(true)}
        title="볼륨 가져오기"
      >
        데이터
      </button>
      {showSetup ? (
        <div className="setup-overlay">
          <SetupPanel
            mock={false}
            modelReady
            allowSkip
            status={{
              phase: setupPhase,
              bytes: 0,
              totalBytes: REQUIRED_TOTAL_BYTES,
              showCorpus: true,
            }}
            volumes={setupVolumes}
            onStart={pickFirstMissing}
            onComplete={() => setShowSetup(false)}
            onSkip={() => setShowSetup(false)}
            {...volumeHandlers}
          />
        </div>
      ) : null}
    </div>
  );
}

declare global {
  interface Window {
    demoProbe: {
      status: () => Promise<VolumeStatus>;
      volumes: () => VolumeStates | null;
      search: (query: string) => Promise<Dossier>;
      direct: (law: string, article: string) => Promise<unknown>;
      searchCases: (query: string, limit?: number) => Promise<unknown>;
      related: (
        locus: string,
        text: string,
        exclude: string[],
        limit?: number,
        includeDirect?: boolean,
      ) => Promise<unknown>;
      caseByNumber: (caseNo: string, court?: string) => Promise<unknown>;
      caseText: (pointer: string) => Promise<CaseTextDocument>;
    };
  }
}

window.demoProbe = {
  status: () => transport.status(),
  volumes: () => activeStore?.states() ?? null,
  search: (query: string) => provider.search(query, 1),
  direct: (law: string, article: string) => transport.directCases(law, article),
  searchCases: (query: string, limit = 10) => transport.searchCases(query, limit),
  related: (
    locus: string,
    text: string,
    exclude: string[],
    limit = 10,
    includeDirect = false,
  ) => transport.relatedCases(locus, text, exclude, limit, includeDirect),
  caseByNumber: (caseNo: string, court?: string) => transport.caseByNumber(caseNo, court),
  caseText: (pointer: string) => provider.caseText(pointer),
};

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");

createRoot(root).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
